import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ApiError } from "@/api/problem";
import { SessionProvider } from "@/auth/session";
import { I18nProvider } from "@/i18n/provider";
import { createAppRouter } from "@/router";
import "@/styles/global.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Retrying a 401, 403 or 404 only makes the user wait longer for the same answer.
        // A 5xx or a dropped connection is worth one more go.
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      // A mutation is a user's deliberate act. Replaying it silently is how you end up with
      // two refills for one delivery — the server offers Idempotency-Key so we don't have to.
      retry: false,
    },
  },
});

const router = createAppRouter(queryClient);

async function bootstrap() {
  // MSW, while the Go router is still being wired. When the backend answers for real, this
  // flag goes off in the environment and NOT ONE LINE of application code changes: both
  // sides are generated from the same OpenAPI contract.
  if (import.meta.env.VITE_USE_MOCKS === "true") {
    const { startMocks } = await import("@/mocks/browser");
    await startMocks();
  }

  const rootElement = document.getElementById("root");
  if (!rootElement) throw new Error("#root is missing from index.html");

  createRoot(rootElement).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <I18nProvider>
          {/* SessionProvider sits above the router's tree so `useSession()` is available to
              every screen — while the ROUTE GUARDS read the same /v1/me from the query
              cache, without needing React at all. One source of truth, two consumers. */}
          <SessionProvider>
            <RouterProvider router={router} />
          </SessionProvider>
        </I18nProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
}

void bootstrap();
